import React, { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import {
  Box, Grid, Typography, Avatar, Divider, Link, IconButton, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import { CustomChart, ChartSeriesConfig, ChartGroupConfig } from "@/components/chart/CustomChart";
import { MonthlyCarStatSimulationMetric } from "@/models/goFurther";
import { EvExternalChargingRatios, CarTypeMonthlyStats, EvCarMonthlyStats } from "@/models/monthlyCarStats";
import { getUnderuseReasonLabel } from "@/pages/CarUnderuseEvents/CarUnderuseAnswersConstants";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorIcon from "@mui/icons-material/Error";
import NearMeIcon from "@mui/icons-material/NearMe";
import { FONT_SIZE, FONT_WEIGHT, TEXT_COLOR, BACKGROUND_COLOR, COLOR_LPG_LINE } from "../GoFurtherStyles";
import { SectionGridItemCard, SecondaryPanel, SimulationArrowPill, GoFurtherExternalLink } from "./GoFurtherPrimitives";
import {
  formatNumber, formatCurrency, formatPercent,
  formatMileageAxisTick, formatPercentAxisTick,
  type VehicleChartDataItem, computePreviousMonthKey,
} from "../GoFurtherUtils";
import { createGoFurtherTooltip } from "./GoFurtherChartTooltip";

// ── 型定義 ─────────────────────────────────────────────────────────────────────

interface GoFurtherPhaseSectionProps {
  vehicleChartData: VehicleChartDataItem[];
  sortedVehicleIds: string[];
  vehicleLabelById: Record<string, string>;
  lowOperationEv: MonthlyCarStatSimulationMetric | null;
  bestHighOperation: MonthlyCarStatSimulationMetric | null;
  mileageIncreaseValue: number;
  revenueIncreaseValue: number | null;
  grossProfitIncreaseValue: number | null;
  monthlyRevenueImprovementText: string;
  chargingFeeStepText: string;
  /** "B1" | "B2" | "C" | "D" など。B 系統かどうかの判定に使用。 */
  personaType?: string;
  officeName?: string;
  evExternalChargingRatios?: EvExternalChargingRatios | null;
  carTypeMonthlyStats?: CarTypeMonthlyStats | null;
  evCarMonthlyStats?: EvCarMonthlyStats | null;
  selectedYyyymm?: string;
  onChangeYyyymm?: (yyyymm: string) => void;
  isMonthLoading?: boolean;
}

type PersonaBPhaseStatus = {
  currentPhaseText: string;
  futurePlanText: React.ReactNode;
};

// ── ファイルプライベート ────────────────────────────────────────────────────────

const shiftMonth = (yyyymm: string, delta: number): string => {
  let year = parseInt(yyyymm.slice(0, 4), 10);
  let month = parseInt(yyyymm.slice(4, 6), 10) - 1 + delta;
  year += Math.floor(month / 12);
  month = ((month % 12) + 12) % 12;
  return `${year}${String(month + 1).padStart(2, "0")}`;
};

const MonthPickerWithNav: React.FC<{
  value: string;
  onChange: (yyyymm: string) => void;
  min?: string;
  max?: string;
}> = ({ value, onChange, min, max }) => {
  const displayText = value ? `${value.slice(0, 4)}年${value.slice(4, 6)}月` : "----年--月";
  const inputValue = value ? `${value.slice(0, 4)}-${value.slice(4, 6)}` : "";
  const inputMin = min ? `${min.slice(0, 4)}-${min.slice(4, 6)}` : undefined;
  const inputMax = max ? `${max.slice(0, 4)}-${max.slice(4, 6)}` : undefined;

  const handlePrev = () => {
    if (!value) return;
    const prev = shiftMonth(value, -1);
    if (min && prev < min) return;
    onChange(prev);
  };
  const handleNext = () => {
    if (!value) return;
    const next = shiftMonth(value, 1);
    if (max && next > max) return;
    onChange(next);
  };
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace("-", "");
    if (!v) return;
    if (min && v < min) return;
    if (max && v > max) return;
    onChange(v);
  };

  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      border: "1px solid rgba(0,0,0,0.23)",
      borderRadius: 1,
      overflow: "hidden",
    }}>
      <IconButton
        size="small"
        onClick={handlePrev}
        disabled={!value || (!!min && value <= min)}
        sx={{
          borderRadius: 0,
          px: 1,
          alignSelf: "stretch",
          color: "text.secondary",
          "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.2)" },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box sx={{ width: "1px", bgcolor: "rgba(0,0,0,0.12)", alignSelf: "stretch" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}>
        <Typography sx={{ fontSize: FONT_SIZE.body2, whiteSpace: "nowrap", lineHeight: 1 }}>
          {displayText}
        </Typography>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <CalendarMonthIcon sx={{ fontSize: "1rem", color: "text.secondary", display: "block" }} />
          <input
            type="month"
            aria-label="年月を選択"
            value={inputValue}
            min={inputMin}
            max={inputMax}
            onChange={handleInput}
            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
          />
        </Box>
      </Box>

      <Box sx={{ width: "1px", bgcolor: "rgba(0,0,0,0.12)", alignSelf: "stretch" }} />

      <IconButton
        size="small"
        onClick={handleNext}
        disabled={!value || (!!max && value >= max)}
        sx={{
          borderRadius: 0,
          px: 1,
          alignSelf: "stretch",
          color: "text.secondary",
          "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.2)" },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const SimBadge: React.FC<{ label: "A" | "B"; size?: "sm" | "lg" }> = ({ label, size = "sm" }) => {
  const bgcolor = label === "A" ? BACKGROUND_COLOR.WARNING : BACKGROUND_COLOR.INFO;
  const dim = size === "lg" ? 24 : 18;
  const fontSize = size === "lg" ? FONT_SIZE.body2 : FONT_SIZE.caption;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        width: dim,
        height: dim,
        borderRadius: "50%",
        bgcolor,
        color: TEXT_COLOR.CONTRAST,
        alignItems: "center",
        justifyContent: "center",
        fontSize,
        verticalAlign: "middle",
        mx: 0.25,
        ...(size === "lg" ? { border: `1px solid ${bgcolor}` } : {}),
      }}
    >
      {label}
    </Box>
  );
};

const createVehicleTooltip = (lowerFiveKeys: string[], lpgKey: string, valueUnit: string): React.FC<any> => {
  return createGoFurtherTooltip({
    allowedDataKeys: [...lowerFiveKeys, lpgKey],
    getUnit: () => valueUnit,
  });
};

/**
 * A/B1/B2 ごとのフェーズ状態文言。
 * A は B2 と同じ文言を表示する（完全同期かは不透明なため、一旦別定義として扱う）。
 */
const PERSONA_AB_PHASE_STATUS: Record<string, PersonaBPhaseStatus> = {
  A: {
    currentPhaseText: "LPG同等の走行実績を達成できている車両がございます。是非とも全台の達成や増車に向け打ち手をご相談させてください",
    futurePlanText: (
      <>
        下記Aのデータからは、該当車両の稼働状況をご確認いただけます。
        <br />
        下記B・Cのデータで、外部充電の利用実績や稼働状況をご確認いただけます。
        <br />
        好事例の要因を掴み、他車両への展開や増車をぜひご検討ください。
      </>
    ),
  },
  B1: {
    currentPhaseText: "LPG同等に向けて十分に伸びしろがありますので、是非とも打ち手をご相談させてください",
    futurePlanText: (
      <>
        下記B・Cのデータで、各車両別に外部充電の利用実績や稼働状況をご確認いただけます。
        <br />
        例えば外部充電活用の周知など、まずは特定の車両でのLPG同等の実現を目指した取組みをぜひご検討ください。
      </>
    ),
  },
  B2: {
    currentPhaseText: "LPG同等の走行実績を達成できている車両がございます。是非とも全台の達成や増車に向け打ち手をご相談させてください",
    futurePlanText: (
      <>
        下記Aのデータからは、該当車両の稼働状況をご確認いただけます。
        <br />
        下記B・Cのデータで、外部充電の利用実績や稼働状況をご確認いただけます。
        <br />
        好事例の要因を掴み、他車両への展開や増車をぜひご検討ください。
      </>
    ),
  },
};

/** セクションCテーブル: この行数を超えたらヘッダー固定＋ボディスクロールに切り替える */
const TABLE_SCROLL_ROW_THRESHOLD = 14;
/** ヘッダー行の高さ (px) */
const TABLE_HEADER_ROW_HEIGHT_PX = 40;
/** ボディ行の高さ (px) */
const TABLE_BODY_ROW_HEIGHT_PX = 36;
/**
 * スクロール時の最大高さ。
 * ヘッダー1行 + (閾値 - 0.5)行分の高さにすることで、閾値行目が半分見える状態にし、
 * スクロール可能であることをユーザーに示す。
 */
const TABLE_SCROLL_MAX_HEIGHT = TABLE_HEADER_ROW_HEIGHT_PX + Math.round(TABLE_BODY_ROW_HEIGHT_PX * (TABLE_SCROLL_ROW_THRESHOLD - 0.5));

/** カスタムスクロールバーの幅 (px) */
const CUSTOM_SCROLLBAR_WIDTH = 6;

/** 外部充電比率グラフのX軸ラベルを斜め表示に切り替える件数閾値 */
const X_AXIS_ANGLE_THRESHOLD = 13;

/**
 * 外部充電比率グラフの高さ。
 * 車両台数によって高さを変えると、営業所切替のたびに X_AXIS_ANGLE_THRESHOLD を
 * またいで高さがジャンプしちらつきの原因になるため、常に斜め表示分の余白を確保した固定値にする。
 */
const EXTERNAL_CHARGING_CHART_HEIGHT = 340;

/**
 * 3要件を同時に満たすカスタムスクロールバー付きテーブルラッパー。
 * 1. スクロール可能な場合はスクロールバーを常時表示
 * 2. スクロールバー表示前後でテーブル幅が変化しない
 * 3. 非スクロール時に枠線とコンテンツ間に余白が生じない
 *
 * overflow-y:scroll + scrollbar-width:none でネイティブスクロールバーを非表示にしつつ
 * スクロール動作（ホイール・キーボード・タッチ）は維持し、
 * 絶対配置のカスタムスクロールバーをオーバーレイとして描画する。
 */
const TableScrollWrapper: React.FC<{
  scrollable: boolean;
  children: React.ReactNode;
}> = ({ scrollable, children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; startScrollTop: number } | null>(null);

  // setState を使わず直接 DOM を更新することで再レンダーを省きスクロールとの同期遅延を排除する
  const computeThumb = useCallback(() => {
    const el = scrollRef.current;
    const thumbEl = thumbRef.current;
    if (!el || !thumbEl) return;
    if (el.scrollHeight <= el.clientHeight) {
      thumbEl.style.height = "0px";
      return;
    }
    const trackH = el.clientHeight;
    const h = Math.max(24, (trackH / el.scrollHeight) * trackH);
    const t = (el.scrollTop / (el.scrollHeight - trackH)) * (trackH - h);
    thumbEl.style.height = `${h}px`;
    thumbEl.style.top = `${t}px`;
  }, []);

  useLayoutEffect(() => {
    computeThumb();
  }, [scrollable, computeThumb]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !scrollable) return;
    // React 合成イベント経由を避け native passive listener で遅延を排除する
    el.addEventListener("scroll", computeThumb, { passive: true });
    const observer = new ResizeObserver(computeThumb);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", computeThumb);
      observer.disconnect();
    };
  }, [scrollable, computeThumb]);

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    drag.current = { startY: e.clientY, startScrollTop: scrollRef.current?.scrollTop ?? 0 };
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!drag.current || !scrollRef.current) return;
      const el = scrollRef.current;
      const trackH = el.clientHeight;
      const h = Math.max(24, (trackH / el.scrollHeight) * trackH);
      const maxTravel = trackH - h;
      if (maxTravel <= 0) return;
      const dy = e.clientY - drag.current.startY;
      el.scrollTop = drag.current.startScrollTop + (dy / maxTravel) * (el.scrollHeight - trackH);
    };
    const onUp = () => { drag.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <Box sx={{ position: "relative", border: `1px solid ${BACKGROUND_COLOR.BORDER_DARK}` }}>
      <Box
        ref={scrollRef}
        sx={scrollable ? {
          maxHeight: TABLE_SCROLL_MAX_HEIGHT,
          overflowY: "scroll",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        } : undefined}
      >
        {children}
      </Box>
      {scrollable && (
        <Box sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: CUSTOM_SCROLLBAR_WIDTH,
          bgcolor: "rgba(0,0,0,0.06)",
          pointerEvents: "none",
        }}>
          <Box
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            style={{ top: 0, height: 0 }}
            sx={{
              position: "absolute",
              width: "100%",
              bgcolor: "rgba(0,0,0,0.25)",
              borderRadius: 1,
              cursor: "ns-resize",
              userSelect: "none",
              pointerEvents: "auto",
              "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const EMPTY_VALUE = "---";

/** タイトル行 + キャプション群の共通ヘッダー（セクション A・B・C テーブルで使用） */
const PersonaBSectionHeader: React.FC<{
  avatar?: string;
  title: React.ReactNode;
  right?: React.ReactNode;
  descriptions: React.ReactNode[];
}> = ({ avatar, title, right, descriptions }) => (
  <>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {avatar && (
        <Avatar sx={{ bgcolor: BACKGROUND_COLOR.PRIMARY, width: 28, height: 28, fontSize: FONT_SIZE.body2, fontWeight: FONT_WEIGHT.BOLD, flexShrink: 0 }}>
          {avatar}
        </Avatar>
      )}
      <Typography sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
        {title}
      </Typography>
      {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {descriptions.map((desc, i) => (
        <Typography key={i} sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
          {desc}
        </Typography>
      ))}
    </Box>
  </>
);

const SubSectionHeader: React.FC<{
  title: React.ReactNode;
  right?: React.ReactNode;
  descriptions: React.ReactNode[];
}> = ({ title, right, descriptions }) => (
  <>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: FONT_SIZE.headline6 }}>
        {title}
      </Typography>
      {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {descriptions.map((desc, i) => (
        <Typography key={i} sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
          {desc}
        </Typography>
      ))}
    </Box>
  </>
);

/** セクションCテーブル共通スタイル: 縦罫線 */
const TABLE_SX = {
  "& .MuiTableCell-root": { borderRight: `1px solid ${BACKGROUND_COLOR.BORDER_DARK}` },
  "& .MuiTableCell-root:last-child": { borderRight: 0 },
} as const;

/** セクションCテーブル ヘッダーセル共通スタイル */
const HEADER_CELL_SX = {
  bgcolor: BACKGROUND_COLOR.PRIMARY,
  fontWeight: FONT_WEIGHT.BOLD,
  fontSize: FONT_SIZE.subtitle2,
  color: TEXT_COLOR.CONTRAST,
  height: TABLE_HEADER_ROW_HEIGHT_PX,
  py: 0,
  whiteSpace: "nowrap",
} as const;

/** セクションCテーブル ボディセル共通スタイル */
const BODY_CELL_SX = {
  fontWeight: FONT_WEIGHT.BOLD,
  fontSize: FONT_SIZE.subtitle2,
  color: TEXT_COLOR.PRIMARY,
  height: TABLE_BODY_ROW_HEIGHT_PX,
  py: 0,
  whiteSpace: "nowrap",
} as const;

/** 白背景カードスタイル。青背景の SecondaryPanel と対をなす白枠セクション用。 */
const WHITE_SECTION_SX = {
  p: 2,
  borderRadius: "16px",
  bgcolor: BACKGROUND_COLOR.WHITE,
  border: `1px solid ${BACKGROUND_COLOR.INHERIT}`,
  boxShadow: "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
} as const;

// ── フェーズステップコンポーネント ───────────────────────────────────────────────

const PHASE_STEPS = [
  { step: 1, title: "一部車両で\n外部充電器を使用", sub: "充電運用体制の構築" },
  { step: 2, title: "一部車両で\nLPG同等走行を開始", sub: "乗務員様への説明/再配置" },
  { step: 3, title: "全車両で\nLPG同等走行を横展開", sub: "取り組みの継続" },
] as const;

/**
 * CD 向けフェーズ3ステップ。
 * アイコンが左・テキストが右の横並び構造。全ステップを均一表示。
 * フェーズ1バッジ直下から「フェーズ1からの取り組みがオススメです」へ向かう緑の下向き三角を表示。
 */
const PhaseStepsPersonaCD: React.FC = () => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {PHASE_STEPS.map((item, index) => (
        <React.Fragment key={item.step}>
          {index > 0 && (
            <Divider orientation="horizontal" sx={{ width: "47px", flex: "0 0 47px", mx: 1 }} />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "0 0 auto" }}>
            {/* フェーズ1 バッジのみ position: relative にして下向き三角を絶対配置 */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar sx={{ bgcolor: BACKGROUND_COLOR.PRIMARY, width: 36, height: 36, fontSize: FONT_SIZE.body1 }}>
                {item.step}
              </Avatar>
              {item.step === 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "calc(100% + 6px)",
                    transform: "translateX(-50%)",
                    width: "16px",
                    height: "84px",
                    background: "linear-gradient(180deg, rgba(31, 180, 63, 0) 0%, rgba(31, 180, 63, 0.4) 100%)",
                    clipPath: "polygon(0 0, 100% 0, 100% 86%, 50% 100%, 0 86%)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Box>
            <Box>
              <Typography fontWeight="bold" sx={{ fontSize: FONT_SIZE.headline6, whiteSpace: "nowrap" }}>
                {item.title.split("\n").map((line, lineIndex) => (
                  <React.Fragment key={lineIndex}>
                    {lineIndex > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </Typography>
              <Typography sx={{ mt: 0.5, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.body2 }}>
                {item.sub}
              </Typography>
            </Box>
          </Box>
        </React.Fragment>
      ))}
    </Box>
    <Typography sx={{ mt: 3, ml: 7, fontSize: FONT_SIZE.subtitle2, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.SUCCESS }}>
      フェーズ1からの取り組みがオススメです
    </Typography>
  </Box>
);

/**
 * ペルソナB向けフェーズ3ステップ。
 * アイコンが上・テキストが下の縦積み構造。
 * B1/B2 共通: フェーズ1=完了(✓)・フェーズ2=強調(現在)・フェーズ3=通常(未来) 固定。
 */
const PhaseStepsPersonaB: React.FC = () => {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", px: 2 }}>
      {PHASE_STEPS.map((item, index) => {
        const isCompleted = item.step === 1;
        const isCurrent = item.step === 2;

        return (
          <React.Fragment key={item.step}>
            {index > 0 && (
              <Divider orientation="horizontal" sx={{ flex: 1, mx: 2, alignSelf: "center" }} />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                flex: "0 0 auto",
                px: 2,
              }}
            >
              {/* アイコン領域は最大サイズに固定し、アイコンを下寄せで揃える */}
              <Box sx={{ width: 48, height: 48, display: "flex", alignItems: "flex-end", justifyContent: "center", mb: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: BACKGROUND_COLOR.PRIMARY,
                    width: isCurrent ? 48 : 32,
                    height: isCurrent ? 48 : 32,
                    fontSize: isCurrent ? FONT_SIZE.headline5 : FONT_SIZE.body2,
                    fontWeight: FONT_WEIGHT.BOLD,
                  }}
                >
                  {isCompleted ? <CheckIcon sx={{ fontSize: "inherit" }} /> : item.step}
                </Avatar>
              </Box>
              <Typography
                sx={{
                  fontWeight: FONT_WEIGHT.BOLD,
                  fontSize: isCurrent ? FONT_SIZE.headline5 : FONT_SIZE.subtitle1,
                  color: TEXT_COLOR.PRIMARY,
                  whiteSpace: "nowrap",
                  opacity: isCompleted ? 0.5 : 1,
                }}
              >
                {item.title.split("\n").map((line, lineIndex) => (
                  <React.Fragment key={lineIndex}>
                    {lineIndex > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: isCurrent ? FONT_SIZE.body1 : FONT_SIZE.body2, color: TEXT_COLOR.SECONDARY, whiteSpace: "nowrap", opacity: isCompleted ? 0.5 : 1 }}>
                {item.sub}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ── メインコンポーネント ────────────────────────────────────────────────────────

const GoFurtherPhaseSection: React.FC<GoFurtherPhaseSectionProps> = ({
  vehicleChartData,
  sortedVehicleIds,
  vehicleLabelById,
  lowOperationEv,
  bestHighOperation,
  mileageIncreaseValue,
  revenueIncreaseValue,
  grossProfitIncreaseValue,
  monthlyRevenueImprovementText,
  chargingFeeStepText,
  personaType,
  officeName = "",
  evExternalChargingRatios,
  carTypeMonthlyStats,
  evCarMonthlyStats,
  selectedYyyymm,
  onChangeYyyymm,
  isMonthLoading = false,
}) => {
  const isPersonaAB = personaType === "A" || (personaType?.startsWith("B") ?? false);
  const simulationTableColumns = "repeat(6, minmax(0, 1fr))";

  // ── useMemo (共通) ─────────────────────────────────────────────────────────

  const mileageSeries: ChartSeriesConfig[] = useMemo(
    () =>
      sortedVehicleIds.map((id) => ({
        dataKey: `v_${id}_mileage`,
        name: vehicleLabelById[id] || `車番不明 (ID:${id})`,
        type: "line",
        yAxisId: "left",
      })),
    [sortedVehicleIds, vehicleLabelById],
  );

  const mileageSeriesWithOfficeLpg: ChartSeriesConfig[] = useMemo(
    () => [
      ...mileageSeries,
      {
        dataKey: "officeLpgMileage",
        name: "LPG平均",
        type: "line",
        yAxisId: "left",
        color: COLOR_LPG_LINE,
        zIndex: 999,
        selectedZIndex: 1000,
      },
    ],
    [mileageSeries],
  );

  const vehicleGroups: ChartGroupConfig[] = useMemo(
    () => [
      { title: "EV", dataKeys: mileageSeries.map((s) => s.dataKey), selectedStrokeWidth: 4 },
      { title: "LPG", dataKeys: ["officeLpgMileage"], selectedStrokeWidth: 8 },
    ],
    [mileageSeries],
  );

  const lowerFiveVehicleIds = sortedVehicleIds.slice(0, 5);
  const lowerTwoVehicleIds = sortedVehicleIds.slice(0, 2);
  const upperTwoVehicleIds = sortedVehicleIds.slice(-2);

  const mileageLowerFiveKeys = lowerFiveVehicleIds.map((id) => `v_${id}_mileage`);

  const mileageDefaultSelected = useMemo(
    () => [
      "officeLpgMileage",
      ...Array.from(new Set([...lowerTwoVehicleIds, ...upperTwoVehicleIds])).map((id) => `v_${id}_mileage`),
    ],
    [lowerTwoVehicleIds, upperTwoVehicleIds],
  );

  const MileageTooltip = useMemo(
    () => createVehicleTooltip(mileageLowerFiveKeys, "officeLpgMileage", "km"),
    [mileageLowerFiveKeys],
  );

  // ── useMemo (ペルソナB専用) ────────────────────────────────────────────────

  const personaBMileageDefaultSelected = useMemo(() => {
    const goChargeCarIds = (evCarMonthlyStats?.evCars ?? [])
      .filter((car) => car.gochargeCardId != null)
      .map((car) => String(car.carId));
    return ["officeLpgMileage", ...goChargeCarIds.map((id) => `v_${id}_mileage`)];
  }, [evCarMonthlyStats]);

  const externalChargingBarData = useMemo(
    () =>
      (evExternalChargingRatios?.evCarStats ?? []).map((s) => ({
        vehicleCode: s.vehicleCode,
        ratio: s.externalChargingRatio != null ? Math.round(s.externalChargingRatio * 100) : null,
      })),
    [evExternalChargingRatios],
  );

  const externalChargingBarSeries: ChartSeriesConfig[] = useMemo(
    () => [{ dataKey: "ratio", name: "外部充電比率", type: "bar" as const, yAxisId: "left" }],
    [],
  );

  const ExternalChargingTooltip = useMemo(() => {
    const TooltipContent: React.FC<any> = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const entry = payload[0];
      const value = entry?.value != null ? `${Math.round(Number(entry.value))}%` : "-";
      return (
        <Box sx={{
          p: 2,
          bgcolor: BACKGROUND_COLOR.WHITE,
          border: `1px solid ${BACKGROUND_COLOR.INHERIT}`,
          borderRadius: 1.5,
          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
          "& > :not(style) + :not(style)": { mt: 1 },
        }}>
          <Typography sx={{ display: "block", fontSize: FONT_SIZE.caption }}>無線番号：{label}</Typography>
          <Typography sx={{ display: "block", color: entry?.color || "inherit", fontSize: FONT_SIZE.caption }}>
            外部充電比率：{value}
          </Typography>
        </Box>
      );
    };
    return TooltipContent;
  }, []);

  // ── 共通コンテンツ const ───────────────────────────────────────────────────
  //    PersonaCDLayout / PersonaBLayout の両方から参照する。

  const introNode = (
    <Grid item xs={12}>
      <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>
        最初から全てのEVのLPG同等化に取り組んで頂く事も可能ですが、まずは1台からでもお試しいただき、
        <br />
        実際の感触を掴み、徐々に全車両へと展開されている事業者様が多くなっています。
      </Typography>
    </Grid>
  );

  const renderMileageChart = (defaultSelected: string[]) => (
    <CustomChart
      title={
        <Typography sx={{ p: 2, fontSize: FONT_SIZE.headline6 }}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <NearMeIcon sx={{ fontSize: "2rem", color: "text.secondary" }} />
            <Box component="span">
              車両ごとの
              <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>走行実績の推移</Box>
              (月別・車両1台あたり)
            </Box>
          </Box>
        </Typography>
      }
      description={
        <>
          <Typography component="p" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
            走行距離は実績となります。
          </Typography>
          <Typography component="p" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
            ※走行距離がゼロの場合、グラフに表示されません。
          </Typography>
        </>
      }
      data={vehicleChartData}
      series={mileageSeriesWithOfficeLpg}
      groups={vehicleGroups}
      defaultSelected={defaultSelected}
      CustomTooltip={MileageTooltip}
      categoryAxis={{ dataKey: "month" }}
      valueAxes={[{ id: "left", orientation: "left", tickFormatter: formatMileageAxisTick }]}
      height={300}
    />
  );

  const simulationNode = lowOperationEv && bestHighOperation ? (
    <Grid item xs={12}>
      <SecondaryPanel>
        <Typography sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>収益(充電コスト・営収)シミュレーション</Typography>
        <Typography sx={{ mt: 2, fontSize: FONT_SIZE.body1 }}>
          例えば無線番号 {lowOperationEv.vehicleCode || "-"} の車両を、外部充電器を活用しLPG同等に改善いただくことで、<br />
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.body1 }}>
            収益(<SimBadge label="A" />-<SimBadge label="B" />)が {grossProfitIncreaseValue != null ? monthlyRevenueImprovementText : EMPTY_VALUE} 円向上
          </Box>
          する可能性があります。
          <br />
          拡張プラン加入でかかる月額費用と充電料金による充電コスト(<SimBadge label="B" />)は増加しますが、<br />
          営収(<SimBadge label="A" />)も増加するため、差分である収益(<SimBadge label="A" />-<SimBadge label="B" />)向上を実現できる可能性があります。
        </Typography>
        <Typography sx={{ mt: 1, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
          ページ冒頭に入力いただいた営収の条件を基にして、外部充電器を活用しLPG同等に改善した際のシミュレーションを行っています。<br />
          走行距離と外部充電比率から算出しており、参考としてお考えください。<br />
          計算式は表の下にある注釈をご確認ください。
        </Typography>
        <Typography sx={{ mt: 2, color: TEXT_COLOR.SECONDARY, textAlign: "right", fontSize: FONT_SIZE.caption }}>
          ※金額はすべて税込で算出
        </Typography>
        <Box sx={{ mt: 1, p: 1, minWidth: 832, borderRadius: 1.5, bgcolor: BACKGROUND_COLOR.WHITE, overflowX: "auto" }}>
          <Box>
            <Box sx={{ display: "grid", gridTemplateColumns: simulationTableColumns, alignItems: "end", borderBottom: `1px solid ${BACKGROUND_COLOR.INHERIT}`, py: 1, color: TEXT_COLOR.PRIMARY }}>
              <Box />
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body2 }}>
                走行距離<br />
                <Box component="span" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}>km/月</Box>
              </Typography>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body2 }}>
                外部充電比率
                <span style={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}> *1</span>
                <br />
                <Box component="span" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}>%</Box>
              </Typography>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body2 }}>
                <SimBadge label="B" />充電コスト<br />
                <Box component="span" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}>月額費用*2+充電料金*3</Box>
              </Typography>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body2 }}>
                <SimBadge label="A" />月間営業収入
                <span style={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}> *4</span>
                <br />
                <Box component="span" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}>円/台</Box>
              </Typography>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body2 }}>
                <SimBadge label="A" />-<SimBadge label="B" />収益<br />
                <Box component="span" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY, fontWeight: FONT_WEIGHT.NORMAL }}>収入-コスト</Box>
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: simulationTableColumns, alignItems: "center", pt: 2 }}>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body1 }}>無線番号<br />{lowOperationEv.vehicleCode || "-"}</Typography>
              <Typography align="center" sx={{ fontSize: FONT_SIZE.body1 }}>{formatNumber(lowOperationEv.mileageKm)}km</Typography>
              <Typography align="center" sx={{ fontSize: FONT_SIZE.body1 }}>{formatPercent(lowOperationEv.externalChargingRatio)}</Typography>
              <Typography align="center" sx={{ fontSize: FONT_SIZE.body1 }}>{formatCurrency(lowOperationEv.totalCost)}</Typography>
              <Typography align="center" sx={{ fontSize: FONT_SIZE.body1 }}>{formatCurrency(lowOperationEv.revenue)}</Typography>
              <Typography align="center" sx={{ fontSize: FONT_SIZE.body1 }}>{formatCurrency(lowOperationEv.grossProfit)}</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: simulationTableColumns, alignItems: "center" }}>
              <Box />
              <Box sx={{ minWidth: 0, display: "flex", justifyContent: "center", overflow: "visible" }}>
                {mileageIncreaseValue > 0 ? (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>
                    {`+ ${formatNumber(mileageIncreaseValue)}km`}
                    <Typography component="span" sx={{ fontSize: FONT_SIZE.subtitle2, ml: 0.5 }}>増やすと・・</Typography>
                  </SimulationArrowPill>
                ) : (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>{EMPTY_VALUE}</SimulationArrowPill>
                )}
              </Box>
              <Box />
              <Box />
              <Box sx={{ minWidth: 0, display: "flex", justifyContent: "center", overflow: "visible" }}>
                {revenueIncreaseValue != null ? (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>
                    {`¥${formatNumber(revenueIncreaseValue)}`}
                    <Typography component="span" sx={{ fontSize: FONT_SIZE.subtitle2, ml: 0.5 }}>UP!</Typography>
                  </SimulationArrowPill>
                ) : (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>{EMPTY_VALUE}</SimulationArrowPill>
                )}
              </Box>
              <Box sx={{ minWidth: 0, display: "flex", justifyContent: "center", overflow: "visible" }}>
                {grossProfitIncreaseValue != null ? (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>
                    {`¥${formatNumber(grossProfitIncreaseValue)}`}
                    <Typography component="span" sx={{ fontSize: FONT_SIZE.subtitle2, ml: 0.5 }}>UP!</Typography>
                  </SimulationArrowPill>
                ) : (
                  <SimulationArrowPill ovalHeight={28} fontSize={FONT_SIZE.subtitle1}>{EMPTY_VALUE}</SimulationArrowPill>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: simulationTableColumns, alignItems: "center", py: 2, borderRadius: 1.5, bgcolor: BACKGROUND_COLOR.SECONDARY }}>
              <Typography align="center" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.body1 }}>LPG同等に改善</Typography>
              <Typography align="center" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>{formatNumber(bestHighOperation.mileageKm)}km</Typography>
              <Typography align="center" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>{formatPercent(bestHighOperation.externalChargingRatio)}</Typography>
              <Typography align="center" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>{formatCurrency(bestHighOperation.totalCost)}</Typography>
              <Typography align="center" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>{formatCurrency(bestHighOperation.revenue)}</Typography>
              <Typography align="center" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>{formatCurrency(bestHighOperation.grossProfit)}</Typography>
            </Box>
          </Box>
        </Box>
        <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption, mt: 2 }}>
          *1 外部充電比率とは、外部充電での充電割合を示します。外部充電は、GO Chargeにご契約いただくことで使用可能です。但し、電費4.5km/kWhとして計算しています。
          <br />
          *2 月額費用は、外部充電器をご使用の場合、月額料金3,800円/台(税抜)が発生します。
          <br />
          *3 充電料金は、総充電量(走行距離÷電費 4.5km/kWh)×外部充電比率を基に、{chargingFeeStepText}。外部充電では、従量単価及び弊社の平均実績に基づき計算しています。
          <br />
          *4 月間営業収入は、(LPG営収÷LPG走行距離)×(EV又はLPG走行距離) で計算しています。LPG営収は、ページ冒頭にて実際の数字をご入力いただけます。
        </Typography>
      </SecondaryPanel>
    </Grid>
  ) : null;

  const maxYyyymm = useMemo(() => computePreviousMonthKey(), []);
  const minYyyymm = useMemo(() => shiftMonth(maxYyyymm, -11), [maxYyyymm]);

  // セクション B・C で共通の月選択 UI。月はすべてのセクションで連動するため、同一 JSX を複数箇所に配置している。
  const MonthPicker = (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {isMonthLoading && <CircularProgress size={16} thickness={5} sx={{ color: "text.secondary", flexShrink: 0 }} />}
      <MonthPickerWithNav
        value={selectedYyyymm ?? ""}
        onChange={(yyyymm) => onChangeYyyymm?.(yyyymm)}
        min={minYyyymm}
        max={maxYyyymm}
      />
    </Box>
  );

  /** セクションA: EVの稼働データ。AB/CD 共通。太字の番号が指す車両の説明（vehicleFootnote）のみ persona ごとに異なる。 */
  const renderSectionA = (defaultSelected: string[], vehicleFootnote: React.ReactNode) => (
    <Grid item xs={12}>
      <Box sx={WHITE_SECTION_SX}>
        <PersonaBSectionHeader
          avatar="A"
          title={<>{officeName}様の<Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: "inherit", fontWeight: "inherit" }}>EVの稼働データ</Typography></>}
          descriptions={[
            "走行距離について、LPG車両とEV車両を比較しています。",
            vehicleFootnote,
          ]}
        />
        {renderMileageChart(defaultSelected)}
      </Box>
    </Grid>
  );

  /** セクションC: 稼働実績詳細。AB/CD 共通。CD ではセクションBの枠を使うため avatar のみ異なる。 */
  const renderSectionC = (avatar: "B" | "C") => (
    <Grid item xs={12}>
      <Box sx={WHITE_SECTION_SX}>
        <PersonaBSectionHeader
          avatar={avatar}
          title={<>{officeName}様の<Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: "inherit", fontWeight: "inherit" }}>稼働実績詳細</Typography></>}
          descriptions={[]}
        />

        {/* C-1: 車種ごとの稼働実績テーブル */}
        <Box sx={WHITE_SECTION_SX}>
          <SubSectionHeader
            title="車種ごとの稼働実績（月別）"
            right={selectedYyyymm && MonthPicker}
            descriptions={[
              "車種ごとに月間走行距離と平均ログイン時間を比較しています。",
              "※ログイン時間は、ログイン・開局時間から、ログアウト・閉局時間までの合計時間を、暦日で割った平均となります。",
            ]}
          />
          <Box sx={{ opacity: isMonthLoading ? 0.4 : 1, transition: "opacity 0.15s" }}>
            <TableScrollWrapper
              scrollable={(carTypeMonthlyStats?.carTypeStats.length ?? 0) >= TABLE_SCROLL_ROW_THRESHOLD}
            >
              <Table
                size="small"
                stickyHeader={(carTypeMonthlyStats?.carTypeStats.length ?? 0) >= TABLE_SCROLL_ROW_THRESHOLD}
                sx={TABLE_SX}
              >
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={HEADER_CELL_SX}>車種</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>台数</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>月間走行距離</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>平均ログイン時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(carTypeMonthlyStats?.carTypeStats ?? []).map((stat) => (
                    <TableRow key={stat.carTypeName} sx={{ bgcolor: stat.isEv ? BACKGROUND_COLOR.HIGHLIGHT : BACKGROUND_COLOR.WHITE, "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell align="center" sx={BODY_CELL_SX}>{stat.carTypeName}</TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>{stat.carCount}台</TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {stat.avgMileageKm != null ? `${Math.round(stat.avgMileageKm).toLocaleString("ja-JP")}km` : "-"}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {stat.avgDailyLoginSessionHours != null ? `${stat.avgDailyLoginSessionHours.toFixed(1)}時間` : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!carTypeMonthlyStats || carTypeMonthlyStats.carTypeStats.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.body2 }}>
                        {carTypeMonthlyStats ? "該当月のデータがありません" : "データを読み込み中です"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableScrollWrapper>
          </Box>
        </Box>

        {/* C-2: EV車両の稼働実績テーブル */}
        <Box sx={WHITE_SECTION_SX}>
          <SubSectionHeader
            title="EV車両の稼働実績（月別・車両1台あたり）"
            right={selectedYyyymm && MonthPicker}
            descriptions={[
              "EV車両の月間走行距離と平均ログイン時間を比較しています。",
              <>※車両とGO Chargeカードを紐づけることで、一目で拡張プランを利用している車両がわかります。<br />※紐づけは、<GoFurtherExternalLink href="/cars">こちら</GoFurtherExternalLink>の車両ページの詳細＞編集ボタンから可能ですが、不明な場合は<Link href="mailto:gx_contact@goinc.jp" underline="none" sx={{ color: "inherit" }}>gx_contact@goinc.jp</Link>にご連絡ください。</>,
            ]}
          />
          <Box sx={{ opacity: isMonthLoading ? 0.4 : 1, transition: "opacity 0.15s" }}>
            <TableScrollWrapper
              scrollable={(evCarMonthlyStats?.evCars.length ?? 0) >= TABLE_SCROLL_ROW_THRESHOLD}
            >
              <Table
                size="small"
                stickyHeader={(evCarMonthlyStats?.evCars.length ?? 0) >= TABLE_SCROLL_ROW_THRESHOLD}
                sx={TABLE_SX}
              >
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={HEADER_CELL_SX}>車種</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>無線番号</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>GO Chargeカード番号*</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>月間走行距離</TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>平均ログイン時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(evCarMonthlyStats?.evCars ?? []).map((car) => {
                    const hasCard = car.gochargeCardId != null;
                    const underuseTitle = "未稼働/低稼働の車両";
                    const underuseDescription = car.underuseReason != null
                      ? `理由：${getUnderuseReasonLabel(car.underuseReason)}`
                      : null;
                    return (
                      <TableRow key={car.carId} sx={{ bgcolor: hasCard ? BACKGROUND_COLOR.HIGHLIGHT : BACKGROUND_COLOR.WHITE, "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell align="center" sx={BODY_CELL_SX}>{car.carTypeName}</TableCell>
                        <TableCell align="center" sx={BODY_CELL_SX}>
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            {car.vehicleCode}
                            {car.isUnderused && (
                              <Tooltip
                                placement="right"
                                title={
                                  <>
                                    {underuseTitle}
                                    {underuseDescription != null && (
                                      <>
                                        <br />
                                        {underuseDescription}
                                      </>
                                    )}
                                  </>
                                }
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: BACKGROUND_COLOR.WHITE,
                                      color: TEXT_COLOR.PRIMARY,
                                      borderRadius: 2,
                                      px: 2,
                                      py: 1.5,
                                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                      fontSize: FONT_SIZE.body2,
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  aria-label={underuseDescription != null ? `${underuseTitle}（${underuseDescription}）` : underuseTitle}
                                  size="small"
                                  disableRipple
                                  sx={{ p: 0 }}
                                >
                                  <ErrorIcon sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.SECONDARY }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={BODY_CELL_SX}>
                          {car.gochargeCardNo != null ? (
                            <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>{car.gochargeCardNo}</Box>
                          ) : "-"}
                        </TableCell>
                        <TableCell align="center" sx={BODY_CELL_SX}>
                          {car.mileageKm != null ? `${Math.round(car.mileageKm).toLocaleString("ja-JP")}km` : "-"}
                        </TableCell>
                        <TableCell align="center" sx={BODY_CELL_SX}>
                          {car.dailyLoginSessionHours != null ? `${car.dailyLoginSessionHours.toFixed(1)}時間` : "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!evCarMonthlyStats || evCarMonthlyStats.evCars.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.body2 }}>
                        {evCarMonthlyStats ? "該当月のデータがありません" : "データを読み込み中です"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableScrollWrapper>
          </Box>
        </Box>
      </Box>
    </Grid>
  );

  // ── CD レイアウト ──────────────────────────────────────────────────────────

  const PersonaCDLayout = (
    <>
      {introNode}
      <Grid item xs={12}>
        <PhaseStepsPersonaCD />
        <SecondaryPanel sx={{ borderRadius: 2, mt: 3 }}>
          <Typography
            sx={{
              color: TEXT_COLOR.CONTRAST,
              bgcolor: BACKGROUND_COLOR.PRIMARY,
              display: "inline-block",
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontSize: FONT_SIZE.body2,
              fontWeight: FONT_WEIGHT.BOLD,
            }}
          >
            1台からの運用強化
          </Typography>
          <Typography sx={{ mt: 1, color: TEXT_COLOR.PRIMARY, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline6 }}>
            改善対象としておすすめの
            <Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: "inherit", fontSize: "inherit" }}>車両</Typography>
          </Typography>
          <Box mt={1}>
            <Typography component="p" sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>
              LPG車両の稼働を大きく下回っているEV車両は改善対象となります。
              <br />
              または、現在稼働が高い車両の乗務員様に外部充電器活用で稼働時間を増やしていただくことで、より営収を上げていただける可能性もあります。
            </Typography>
            <Typography component="p" mt={1} sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>
              改善対象としておすすめの車両は、以下のグラフにて選択できます。
            </Typography>
          </Box>
        </SecondaryPanel>
      </Grid>
      {renderSectionA(
        mileageDefaultSelected,
        <>※太字の番号は営業所内で下位・上位2件の車両、赤色の折れ線はLPG車両です。<br />※紐づけは、<GoFurtherExternalLink href="/cars">こちら</GoFurtherExternalLink>の車両ページの詳細＞編集ボタンから可能ですが、不明な場合は<Link href="mailto:gx_contact@goinc.jp" underline="none" sx={{ color: "inherit" }}>gx_contact@goinc.jp</Link>にご連絡ください。</>,
      )}
      {renderSectionC("B")}
      {simulationNode}
    </>
  );

  // ── ペルソナBレイアウト ────────────────────────────────────────────────────

  const phaseStatus = personaType ? (PERSONA_AB_PHASE_STATUS[personaType] ?? null) : null;

  const PersonaBLayout = (
    <>
      {introNode}

      {/* フェーズ3ステップ */}
      <Grid item xs={12}>
        <PhaseStepsPersonaB />
      </Grid>

      {/* B1/B2 ごとのフェーズ状態（1枚の SecondaryPanel にまとめる） */}
      {phaseStatus && (
        <Grid item xs={12}>
          <SecondaryPanel>
            <Typography
              component="span"
              sx={{
                color: TEXT_COLOR.CONTRAST,
                bgcolor: BACKGROUND_COLOR.PRIMARY,
                display: "inline-block",
                px: 2,
                py: 0.5,
                borderRadius: 16,
                fontSize: FONT_SIZE.subtitle2,
                fontWeight: FONT_WEIGHT.BOLD,
              }}
            >
              貴社の現状
            </Typography>
            <Typography sx={{ mt: 1, mb: 2.5, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.subtitle1, fontWeight: FONT_WEIGHT.BOLD }}>
              {phaseStatus.currentPhaseText}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: TEXT_COLOR.CONTRAST,
                bgcolor: BACKGROUND_COLOR.PRIMARY,
                display: "inline-block",
                px: 2,
                py: 0.5,
                borderRadius: 16,
                fontSize: FONT_SIZE.subtitle2,
                fontWeight: FONT_WEIGHT.BOLD,
              }}
            >
              詳細と方針案
            </Typography>
            <Typography sx={{ mt: 1, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.subtitle1, fontWeight: FONT_WEIGHT.BOLD }}>
              {phaseStatus.futurePlanText}
            </Typography>
          </SecondaryPanel>
        </Grid>
      )}

      {/* セクションA: EVの稼働データ */}
      {renderSectionA(
        personaBMileageDefaultSelected,
        <>※太字の番号はGO Chargeカードを紐づけている車両、赤色の折れ線はLPG車両です。<br />※紐づけは、<GoFurtherExternalLink href="/cars">こちら</GoFurtherExternalLink>の車両ページの詳細＞編集ボタンから可能ですが、不明な場合は<Link href="mailto:gx_contact@goinc.jp" underline="none" sx={{ color: "inherit" }}>gx_contact@goinc.jp</Link>にご連絡ください。</>,
      )}

      {/* セクションB: EVの外部充電利用実績 */}
      <Grid item xs={12}>
        <Box sx={WHITE_SECTION_SX}>
          <PersonaBSectionHeader
            avatar="B"
            title={<>{officeName}様の<Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: "inherit", fontWeight: "inherit" }}>EVの外部充電利用実績</Typography></>}
            descriptions={[
              "営業所の充電器と街中の外部充電器の充電量を車両ごとに比較しています。",
              "※充電量は、車両のOBD2から「充電していると思われる期間」を特定し、残充電量の変化量を基に算出しています。",
            ]}
          />
          {/*
            データが空の間だけ小さい「データなし」メッセージに丸ごと差し替えると、
            営業所切替直後(データリセット直後)〜取得完了までの間だけ高さが大きく縮み、
            取得完了時に元の高さへ戻るちらつきが発生する。
            そのため常にCustomChart(固定高さ)を描画し続け、データが空のときは
            同じ高さのオーバーレイでメッセージのみ差し替える。
          */}
          <Box sx={{ opacity: isMonthLoading ? 0.4 : 1, transition: "opacity 0.15s", position: "relative" }}>
            <CustomChart
              title={
                <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                  <Typography sx={{ fontSize: FONT_SIZE.headline6, flex: 1 }}>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                      <Box component="span">
                        車両ごとの
                        <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>外部充電比率</Box>
                        （月別）
                      </Box>
                    </Box>
                  </Typography>
                  {selectedYyyymm && MonthPicker}
                </Box>
              }
              data={externalChargingBarData}
              series={externalChargingBarSeries}
              CustomTooltip={ExternalChargingTooltip}
              categoryAxis={externalChargingBarData.length >= X_AXIS_ANGLE_THRESHOLD
                ? { dataKey: "vehicleCode", angle: -45, tickMargin: 8, textAnchor: "end" }
                : { dataKey: "vehicleCode" }
              }
              valueAxes={[{ id: "left", orientation: "left", tickFormatter: formatPercentAxisTick, domain: [0, 100], ticks: [0, 25, 50, 75, 100] }]}
              cartesianGrid={{ horizontal: false, vertical: false }}
              referenceLines={[0, 25, 50, 75, 100].map((y) => ({ y, yAxisId: "left", stroke: "#e0e0e0" }))}
              // 営業所ごとに車両台数が変わり、しきい値をまたぐたびに高さが増減してちらつきの原因になるため、
              // X軸ラベルを斜め表示する場合に必要な余白を見込んだ高さに固定する
              height={EXTERNAL_CHARGING_CHART_HEIGHT}
            />
            {externalChargingBarData.length === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: BACKGROUND_COLOR.WHITE,
                  borderRadius: "16px",
                }}
              >
                <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.body2 }}>
                  該当月の外部充電データがありません
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>

      {/* セクションC: 稼働実績詳細 */}
      {renderSectionC("C")}

      {simulationNode}

    </>
  );

  // ── 上段分岐 ──────────────────────────────────────────────────────────────

  return (
    <SectionGridItemCard
      title={
        <Typography component="h2" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
          LPG同等実現までの
          <Typography
            component="span"
            sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline4, ml: 0.5 }}
          >
            3フェーズ
          </Typography>
        </Typography>
      }
    >
      {isPersonaAB ? PersonaBLayout : PersonaCDLayout}
    </SectionGridItemCard>
  );
};

export default GoFurtherPhaseSection;
